import { useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquareWarning } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useRole } from "@/data/RoleContext";
import { useFrictionLog } from "@/data/FrictionLogContext";
import {
  FRICTION_TYPES, FRICTION_SEVERITIES, MODULE_NAMES,
  detectModuleFromRoute, detectJobIdFromRoute,
} from "@/data/frictionLogTypes";
import type { FrictionType, FrictionSeverity } from "@/data/frictionLogTypes";
import { toast } from "sonner";

/**
 * Global floating "Report Friction" button — renders fixed bottom-right.
 * Auto-detects current module, screen route, and jobId from the URL.
 */
export function GlobalFrictionButton() {
  const [open, setOpen] = useState(false);
  const { role } = useRole();
  const { addEntry } = useFrictionLog();
  const location = useLocation();

  const autoModule = detectModuleFromRoute(location.pathname);
  const autoJobId = detectJobIdFromRoute(location.pathname);

  const [frictionType, setFrictionType] = useState<FrictionType>("UX");
  const [severity, setSeverity] = useState<FrictionSeverity>("Medium");
  const [module, setModule] = useState(autoModule);
  const [description, setDescription] = useState("");

  const handleOpen = () => {
    setModule(detectModuleFromRoute(location.pathname));
    setOpen(true);
  };

  const handleSubmit = () => {
    if (!description.trim()) {
      toast.error("Please describe the friction.");
      return;
    }
    addEntry({
      id: crypto.randomUUID(),
      userId: role,
      userRole: role,
      moduleName: module,
      screenName: location.pathname,
      jobId: autoJobId,
      frictionType,
      description: description.trim(),
      severity,
      status: "New",
      createdAt: new Date().toISOString(),
    });
    toast.success("Feedback submitted");
    setDescription("");
    setFrictionType("UX");
    setSeverity("Medium");
    setOpen(false);
  };

  if (location.pathname === "/team-messages") return null;

  return (
    <>
      {/* Floating trigger — bottom-right, subtle */}
      <button
        onClick={handleOpen}
        className="fixed bottom-5 right-5 z-50 flex items-center gap-1.5 px-3 py-2 rounded-full
                   bg-muted/80 hover:bg-muted border border-border shadow-lg
                   text-[11px] font-medium text-muted-foreground hover:text-foreground
                   backdrop-blur-sm transition-all"
        title="Report Friction"
      >
        <MessageSquareWarning className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Friction</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Report Friction</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Help us improve — describe what felt slow, confusing, or broken.
              <span className="block mt-1 text-[11px] text-primary/80">
                We read every report — typically within 24 hours.
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[11px]">Module</Label>
                <Select value={module} onValueChange={(v) => setModule(v)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MODULE_NAMES.map((m) => (
                      <SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[11px]">Type</Label>
                <Select value={frictionType} onValueChange={(v) => setFrictionType(v as FrictionType)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FRICTION_TYPES.map((t) => (
                      <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[11px]">Severity</Label>
                <Select value={severity} onValueChange={(v) => setSeverity(v as FrictionSeverity)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FRICTION_SEVERITIES.map((s) => (
                      <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[11px]">Role</Label>
                <div className="h-8 flex items-center rounded-md border border-input bg-muted/50 px-3 text-xs text-muted-foreground">
                  {role}
                </div>
              </div>
            </div>

            {/* Auto-detected context — read-only */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[11px]">Screen</Label>
                <div className="h-8 flex items-center rounded-md border border-input bg-muted/50 px-3 text-xs text-muted-foreground truncate">
                  {location.pathname}
                </div>
              </div>
              {autoJobId && (
                <div className="space-y-1">
                  <Label className="text-[11px]">Job ID</Label>
                  <div className="h-8 flex items-center rounded-md border border-input bg-muted/50 px-3 text-xs text-muted-foreground">
                    {autoJobId}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-1">
              <Label className="text-[11px]">Description <span className="text-destructive">*</span></Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What felt wrong, slow, or confusing?"
                className="min-h-[80px] text-xs"
                autoFocus
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSubmit}>Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
